import React from 'react';
import { CallButton } from './CallButton';

interface CallHeaderProps {
  title: string;
  incomingCallAvailable?: boolean;
  isCalling: boolean;
  isConnected: boolean;
  hasIncomingCall: boolean;
  hasCustomer: boolean;
  onCall: () => void;
  onJoinCall?: () => void;
  onRejectCall?: () => void;
  onDisconnect: () => void;
  callButtonVariant?: 'green' | 'blue';
  incomingCallerPhone?: string;
  canMakeWhatsAppCall?: boolean;
  callInitiated?: boolean; // For outgoing calls that were initiated but not joined yet
}

export function CallHeader({
  title,
  incomingCallAvailable,
  isCalling,
  isConnected,
  hasIncomingCall,
  hasCustomer,
  onCall,
  onJoinCall,
  onRejectCall,
  onDisconnect,
  callButtonVariant = 'green',
  incomingCallerPhone,
  canMakeWhatsAppCall,
  callInitiated,
}: CallHeaderProps) {
  return (
    <div className="flex items-center justify-between border-b bg-white px-4 py-3">
      <div className="flex-1">
        <h3 className="text-sm font-medium text-gray-700">{title}</h3>
        {incomingCallAvailable && (
          <div className="mt-1 flex items-center gap-2">
            <p className="text-xs font-semibold text-blue-600 animate-pulse">
              📞 Incoming call
            </p>
            {incomingCallerPhone && (
              <p className="text-xs text-gray-600">
                from <span className="font-medium text-gray-800">{incomingCallerPhone}</span>
              </p>
            )}
            <p className="text-xs text-blue-600">
              - Click "Join Call" to connect
            </p>
          </div>
        )}
        {callInitiated && !isConnected && !isCalling && (
          <div className="mt-1 flex items-center gap-2">
            <p className="text-xs font-semibold text-blue-600 animate-pulse">
              📞 Waiting for call to be answered...
            </p>
            <p className="text-xs text-gray-600">
              Call initiated. Waiting for the other person to join...
            </p>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {isCalling ? (
          <CallButton
            variant={callButtonVariant}
            onClick={() => {}}
            disabled={true}
            label="Connecting..."
          />
        ) : isConnected ? (
          <CallButton
            variant="red"
            onClick={onDisconnect}
            label="End Call"
          />
        ) : hasIncomingCall && incomingCallAvailable ? (
          <>
            {onJoinCall && (
              <CallButton
                variant="green"
                onClick={onJoinCall}
                label="Join Call"
              />
            )}
            {onRejectCall && (
              <CallButton
                variant="red"
                onClick={onRejectCall}
                label="Reject"
              />
            )}
          </>
        ) : callInitiated && onJoinCall ? (
          <CallButton
            variant={callButtonVariant}
            onClick={onJoinCall}
            label="Join Call"
            disabled={true}
            title="Waiting for call to be answered. The system will automatically join when the other person answers."
          />
        ) : (
          <CallButton
            variant={callButtonVariant}
            onClick={onCall}
            disabled={!hasCustomer || (canMakeWhatsAppCall !== undefined && !canMakeWhatsAppCall)}
            title={
              !hasCustomer
                ? "No customer selected"
                : canMakeWhatsAppCall === false
                ? "No incoming calls today. WhatsApp calls are only available when there's an incoming call."
                : undefined
            }
            label="Call"
          />
        )}
      </div>
    </div>
  );
}
